import React from 'react';
import { MapPin, Clock, User, Wrench, Image as ImageIcon, Tag, Eye, Edit2, Lock, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { az } from 'date-fns/locale';

const getTicketAgeStyle = (createdAt) => {
  const now = new Date();
  const created = new Date(createdAt);
  const ageHours = (now - created) / (1000 * 60 * 60);

  if (ageHours > 72) {
    return {
      bg: 'bg-red-50',
      border: 'border-red-300 border-l-4',
      text: 'text-red-900',
      badge: 'bg-red-100 text-red-800'
    };
  } else if (ageHours > 24) {
    return {
      bg: 'bg-yellow-50',
      border: 'border-yellow-300 border-l-4',
      text: 'text-yellow-900',
      badge: 'bg-yellow-100 text-yellow-800'
    };
  }
  return {
    bg: 'bg-white',
    border: 'border-zinc-200',
    text: 'text-zinc-900',
    badge: 'bg-zinc-100 text-zinc-800'
  };
};

const getStatusBadge = (status) => {
  const badges = {
    pending: { label: 'Gözləyir', color: 'bg-zinc-500' },
    assigned: { label: 'Təyin olundu', color: 'bg-blue-500' },
    in_progress: { label: 'Davam edir', color: 'bg-purple-500' },
    completed: { label: 'Tamamlandı', color: 'bg-green-500' },
    closed: { label: 'Bağlandı', color: 'bg-zinc-700' },
    rejected: { label: 'Geri qaytarıldı', color: 'bg-red-500' }
  };
  const badge = badges[status] || badges.pending;
  return (
    <span className={`status-badge ${badge.color} text-white`} data-testid={`status-badge-${status}`}>
      {badge.label}
    </span>
  );
};

export default function TicketCard({ ticket, children, compact = false, hideTechnician = false, onCardClick }) {
  const ageStyle = getTicketAgeStyle(ticket.created_at);

  return (
    <div 
      className={`${ageStyle.bg} ${ageStyle.border} rounded-lg p-4 shadow-sm ticket-card-transition border ${onCardClick ? 'cursor-pointer hover:shadow-md' : ''}`}
      data-testid={`ticket-card-${ticket.id}`}
      onClick={(e) => {
        // Prevent card click if clicking on a button or interactive element
        if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[data-no-card-click]')) {
          return;
        }
        if (onCardClick) {
          onCardClick(ticket);
        }
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-mono text-xs text-zinc-600" data-testid={`ticket-id-${ticket.id}`}>
              #{ticket.id.slice(0, 8)}
            </span>
            {getStatusBadge(ticket.status)}
            {ticket.group_name && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full" data-testid={`ticket-group-${ticket.id}`}>
                <Tag className="h-3 w-3" />
                {ticket.group_name}
              </span>
            )}
          </div>
          
          {!compact && (
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-zinc-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-zinc-900" data-testid={`subscriber-name-${ticket.id}`}>
                    {ticket.subscriber_name}
                  </p>
                  <p className="text-sm text-zinc-600">{ticket.subscriber_phone}</p>
                  {ticket.subscriber_phone_2 && (
                    <p className="text-sm text-zinc-500">Əlavə: {ticket.subscriber_phone_2}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-zinc-600 mt-0.5" />
                <p className="text-sm text-zinc-700" data-testid={`location-${ticket.id}`}>
                  {ticket.subscriber_location}
                </p>
              </div>
              
              <div className="flex items-start gap-2">
                <Wrench className="h-4 w-4 text-zinc-600 mt-0.5" />
                <p className="text-sm text-zinc-700" data-testid={`problem-${ticket.id}`}>
                  {ticket.problem_description}
                </p>
              </div>
              
              {!hideTechnician && ticket.assigned_technician_name && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-600" />
                  <p className="text-sm text-blue-700 font-semibold" data-testid={`assigned-tech-${ticket.id}`}>
                    Texnik: {ticket.assigned_technician_name}
                  </p>
                </div>
              )}
              
              {/* Show scheduled date/time for assigned tickets */}
              {ticket.status === 'assigned' && ticket.scheduled_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  <p className="text-sm text-purple-700 font-semibold">
                    Plan: {ticket.scheduled_date} {ticket.scheduled_time && `saat ${ticket.scheduled_time}`}
                  </p>
                </div>
              )}
              
              {ticket.images && ticket.images.length > 0 && (
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-green-600" />
                  <p className="text-sm text-green-700 font-semibold" data-testid={`images-count-${ticket.id}`}>
                    {ticket.images.length} şəkil
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="text-right ml-4">
          <div className="flex items-center gap-1 text-xs text-zinc-600">
            <Clock className="h-3 w-3" />
            <span data-testid={`ticket-age-${ticket.id}`}>
              {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true, locale: az })}
            </span>
          </div>
        </div>
      </div>
      
      {children}
    </div>
  );
}
